import { useState } from 'react'
import type { CSSProperties, FormEvent } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { api, ApiError } from '../lib/api'
import type { DraftResponse, DraftState, TeamState } from '../types/protocol'
import OrderRandomizer from './OrderRandomizer'

interface Props {
  draft: DraftState
  commissionerToken: string
  onDraft: (draft: DraftState) => void
}

export default function TeamListEditor({ draft, commissionerToken, onDraft }: Props) {
  const [newName, setNewName] = useState('')
  const [newOwner, setNewOwner] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editOwner, setEditOwner] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Two different reasons the order can't move, one meaning to whoever is
  // looking at the screen. Kept separate because only one of them is worth
  // explaining: on a started draft the redirect in Setup means nobody is here
  // anyway, and telling them "the lottery drew this" when no lottery ever ran
  // would be a second lie replacing the first.
  const orderLocked = draft.lottery_revealed_at !== null
  const locked = draft.status !== 'setup' || orderLocked
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  async function request(path: string, options: Parameters<typeof api>[1]) {
    setBusy(true)
    setError(null)
    try {
      const response = await api<DraftResponse>(path, { ...options, commissionerToken })
      onDraft(response.draft)
      return true
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong')
      return false
    } finally {
      setBusy(false)
    }
  }

  async function addTeam(event: FormEvent) {
    event.preventDefault()
    const name = newName.trim()
    if (!name) return
    // undefined, not '': a blank owner has to leave the column NULL, the way
    // Join does it. An empty string reads as "owner known, and it is nothing"
    // everywhere downstream that falls back on `?? 'Unclaimed'`.
    const body = { name, owner_name: newOwner.trim() || undefined }
    if (await request(`/drafts/${draft.join_code}/teams`, { method: 'POST', body })) {
      setNewName('')
      setNewOwner('')
    }
  }

  function startEditing(team: TeamState) {
    setEditingId(team.id)
    setEditName(team.name)
    setEditOwner(team.owner_name ?? '')
  }

  async function saveEdit(event: FormEvent) {
    event.preventDefault()
    if (editingId === null) return
    const saved = await request(`/drafts/${draft.join_code}/teams/${editingId}`, {
      method: 'PATCH',
      body: { name: editName.trim(), owner_name: editOwner.trim() },
    })
    if (saved) setEditingId(null)
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    // useSortable({ disabled: locked }) should already have prevented this, but
    // the optimistic write below is the thing that produces the visible drag ->
    // 422 -> snap-back, so it gets a guard of its own rather than trusting a
    // prop three components away.
    if (locked) return

    const ids = draft.teams.map((team) => team.id)
    const reordered = arrayMove(ids, ids.indexOf(Number(active.id)), ids.indexOf(Number(over.id)))

    // Optimistic reorder so the row doesn't snap back while the PUT is in
    // flight; the server response then becomes the source of truth.
    const byId = new Map(draft.teams.map((team) => [team.id, team]))
    onDraft({
      ...draft,
      teams: reordered.map((id, index) => ({ ...byId.get(id)!, draft_position: index + 1 })),
    })
    await request(`/drafts/${draft.join_code}/order`, { method: 'PUT', body: { team_ids: reordered } })
  }

  return (
    <section className="panel p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="eyebrow">Teams ({draft.teams.length})</h2>
        <OrderRandomizer draft={draft} commissionerToken={commissionerToken} onDraft={onDraft} />
      </div>

      {draft.teams.length === 0 ? (
        <p className="mt-4 text-sm text-ink-lo">No teams yet — add one below.</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
          <SortableContext items={draft.teams.map((team) => team.id)} strategy={verticalListSortingStrategy}>
            <ul className="mt-4 space-y-2">
              {draft.teams.map((team) =>
                editingId === team.id ? (
                  <li key={team.id} className="rounded-lg border border-signal/40 bg-ink p-3">
                    <form onSubmit={(e) => void saveEdit(e)} className="flex flex-wrap items-center gap-2">
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Team name"
                        className="min-w-40 flex-1 rounded border border-line-strong bg-surface-1 px-2 py-1.5 text-sm"
                      />
                      <input
                        value={editOwner}
                        onChange={(e) => setEditOwner(e.target.value)}
                        placeholder="Owner (optional)"
                        className="min-w-32 flex-1 rounded border border-line-strong bg-surface-1 px-2 py-1.5 text-sm"
                      />
                      <button
                        type="submit"
                        disabled={busy || !editName.trim()}
                        className="rounded bg-signal px-3 py-1.5 text-sm font-semibold text-ink disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="rounded border border-line-strong px-3 py-1.5 text-sm"
                      >
                        Cancel
                      </button>
                    </form>
                  </li>
                ) : (
                  <TeamRow
                    key={team.id}
                    team={team}
                    locked={locked}
                    busy={busy}
                    onEdit={() => startEditing(team)}
                    onRemove={() =>
                      void request(`/drafts/${draft.join_code}/teams/${team.id}`, { method: 'DELETE' })
                    }
                    onRelease={() =>
                      void request(`/drafts/${draft.join_code}/teams/${team.id}/claim`, { method: 'DELETE' })
                    }
                  />
                ),
              )}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <form onSubmit={(e) => void addTeam(e)} className="mt-4 flex flex-wrap gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New team name"
          disabled={locked}
          className="min-w-40 flex-1 rounded border border-line-strong bg-ink px-3 py-2 text-sm"
        />
        <input
          value={newOwner}
          onChange={(e) => setNewOwner(e.target.value)}
          placeholder="Owner (optional)"
          disabled={locked}
          className="min-w-32 flex-1 rounded border border-line-strong bg-ink px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={busy || locked || !newName.trim()}
          className="rounded bg-signal px-4 py-2 text-sm font-semibold text-ink hover:bg-signal-dim disabled:opacity-50"
        >
          Add team
        </button>
      </form>
      {/* text-ink-lo, not text-crit: this is a statement of fact, not a failure.
          The second sentence earns its place — Rename is the only control on
          this panel still live, and without it a disabled row reads as
          "everything here is broken". */}
      {orderLocked && (
        <p className="mt-3 text-sm text-ink-lo">
          The lottery drew this order — it is final. Renaming a team still works.
        </p>
      )}
      {error && <p className="mt-3 text-sm text-crit">{error}</p>}
    </section>
  )
}

interface RowProps {
  team: TeamState
  locked: boolean
  busy: boolean
  onEdit: () => void
  onRemove: () => void
  onRelease: () => void
}

function TeamRow({ team, locked, busy, onEdit, onRemove, onRelease }: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: team.id,
    disabled: locked,
  })
  const style: CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition: transition ?? undefined,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 rounded-lg border border-line bg-ink p-3 ${
        isDragging ? 'z-10 opacity-80 shadow-lg shadow-black/50' : ''
      }`}
    >
      <button
        type="button"
        aria-label={`Reorder ${team.name}`}
        {...attributes}
        {...listeners}
        disabled={locked}
        className="cursor-grab touch-none text-ink-lo hover:text-ink-mid disabled:cursor-default"
      >
        ⠿
      </button>
      <span className="w-6 text-center font-mono text-sm text-ink-lo">{team.draft_position}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{team.name}</p>
        {team.owner_name && <p className="truncate text-xs text-ink-lo">{team.owner_name}</p>}
      </div>
      {team.claimed ? (
        <>
          <span className="rounded-full bg-signal/10 px-2 py-0.5 text-xs font-medium text-signal">
            Joined
          </span>
          <button
            type="button"
            onClick={onRelease}
            disabled={busy}
            title="Clear this claim so another device can take the team"
            className="text-xs text-ink-mid underline-offset-2 hover:underline disabled:opacity-50"
          >
            Release
          </button>
        </>
      ) : (
        <span className="rounded-full bg-surface-3 px-2 py-0.5 text-xs text-ink-mid">Open</span>
      )}
      <button
        type="button"
        onClick={onEdit}
        disabled={busy}
        className="text-xs text-ink-mid underline-offset-2 hover:underline disabled:opacity-50"
      >
        Rename
      </button>
      <button
        type="button"
        onClick={onRemove}
        disabled={busy || locked}
        className="text-xs text-crit underline-offset-2 hover:underline disabled:opacity-50"
      >
        Remove
      </button>
    </li>
  )
}
